'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Radio as RACRadio, type RadioProps as RACRadioProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

export interface RadioProps
  extends CommonProps, AriaLabelingProps, Pick<RACRadioProps, 'value' | 'isDisabled' | 'autoFocus'> {
  /** The label rendered next to the radio. */
  children?: StringLikeChildren;
}

function Radio(
  { children, UNSAFE_className, UNSAFE_style, ...props }: RadioProps,
  ref: ForwardedRef<HTMLLabelElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!children && !ariaLabel && !ariaLabelledBy) {
      console.warn('Radio requires one of children / aria-label / aria-labelledby for accessibility');
    }
  }, [children, ariaLabel, ariaLabelledBy]);

  return (
    <RACRadio {...props} className={clsx('cim-radio', UNSAFE_className)} style={UNSAFE_style} ref={ref}>
      <div className="cim-radio-toggle-wrapper">
        <div className="cim-radio-toggle">
          <div className="cim-radio-toggle-inner" />
        </div>
      </div>
      {children && (
        <Text as="span" variant="medium">
          {children}
        </Text>
      )}
    </RACRadio>
  );
}

/**
 * Renders a single option within `RadioGroup`.
 *
 * See [radio group usage guidelines](https://ui.cimpress.io/components/radio-group/).
 */
const _Radio = withStyleProps(forwardRef(Radio), 'Radio');

export { _Radio as Radio };
